import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import "bootstrap/dist/css/bootstrap.min.css";
// Pages
import ImprovedHome from './pages/ImprovedHome';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from "./pages/ResetPassword";
import SuperAdminPortal from './pages/SuperAdminPortal';
import AdminPortal from './pages/AdminPortal';
import SubAdminPortal from './pages/SubAdminPortal';
import OrgUserPortal from './pages/OrgUserPortal';
import ContactUs from './pages/ContactUs';
import Industries from './pages/Industries';
import Services from './pages/Services';
import AboutUs from './pages/AboutUs';
import Career from './pages/Career';
import HowItWorks from './pages/HowItWorks';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import FAQ from './pages/FAQ';
// Common Components
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import SessionManager from './components/common/SessionManager';
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
function LayoutWrapper({ children }) {
  const location = useLocation();
  
  // Check if current route is a dashboard/portal route (logged-in pages)
  const isDashboardRoute = 
    location.pathname.startsWith('/super-admin') ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/sub-admin') ||
    location.pathname.startsWith('/orguser');

  return (
    <>
      <Navbar />
      {/* SessionManager handles auto-logout after inactivity and clears session on tab close */}
      <SessionManager timeout={10 * 60 * 1000} />
       <ToastContainer
        position="top-right"
        autoClose={4000}
        newestOnTop
        closeOnClick
        pauseOnHover
      />
      {children}
      {/* Hide footer on dashboard/logged-in pages */}
      {!isDashboardRoute && <Footer />}
    </>
  );
}

const ProtectedRoute = ({ children, allowedRoles }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    const role = sessionStorage.getItem('role') || localStorage.getItem('role');

    if (token && role) {
      setIsAuthenticated(true);
      // Normalize role to match Sidebar.jsx
      const normalizedRole = 
        role.toLowerCase() === "superadmin" ? "SuperAdmin" : 
        role.toLowerCase() === "admin" ? "Admin" :
        role.toLowerCase() === "subadmin" ? "SubAdmin" :
        role.toLowerCase() === "user" ? "User" :
        role.toLowerCase() === "orguser" ? "OrgUser" :
        role;
      setUserRole(normalizedRole);
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  if (isAuthenticated === null) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(userRole)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  return (
    <Router>
      <LayoutWrapper>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<ImprovedHome />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/industries" element={<Industries />} />
          <Route path="/services" element={<Services />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/career" element={<Career />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="/faq" element={<FAQ />} />
          {/* Super Admin Dashboard */}
          <Route
            path="/super-admin/*"
            element={
              <ProtectedRoute allowedRoles={['SuperAdmin']}>
                <SuperAdminPortal />
              </ProtectedRoute>
            }
          />

          {/* Admin Dashboard */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute allowedRoles={['Admin']}>
                <AdminPortal />
              </ProtectedRoute>
            }
          />

          {/* SubAdmin Dashboard */}
          <Route
            path="/sub-admin/*"
            element={
              <ProtectedRoute allowedRoles={['SubAdmin']}>
                <SubAdminPortal />
              </ProtectedRoute>
            }
          />

          {/* OrgUser Dashboard */}
          <Route
            path="/orguser/*"
            element={
              <ProtectedRoute allowedRoles={['OrgUser']}>
                <OrgUserPortal />
              </ProtectedRoute>
            }
          />

          {/* Logout Route */}
          <Route path="/logout" element={<Navigate to="/login" replace />} />

          {/* Fallback route */}
          <Route path="*" element={<h2>404 - Page Not Found</h2>} />
        </Routes>
      </LayoutWrapper>
    </Router>
  );
}

export default App;